import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isDicomFile } from '../services/dicomLoader';
import uploadService from '../services/uploadService';
import apiClient from '../services/apiClient';
import dicomParser from 'dicom-parser';
import { makeStyles } from '@material-ui/core/styles';
import Icon from '@mdi/react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  LinearProgress,
} from '@material-ui/core';
import {
  mdiArrowLeft,
  mdiLogout,
  mdiFolderOpen,
  mdiCheckCircleOutline ,
  mdiAlertCircleOutline,
} from '@mdi/js';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  headerRight: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  content: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    overflow: 'auto',
  },
  chooseFolderButton: {
    padding: theme.spacing(2, 4),
    fontSize: '1.1rem',
    textTransform: 'none',
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  statusBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  countText: {
    fontSize: '1.2rem',
    fontWeight: 500,
  },
  tableWrapper: {
    width: '100%',
    maxWidth: 1200,
  },
  chooseAnotherButton: {
    textTransform: 'none',
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    width: '100%',
  },
  tableHeaderRow: {
    backgroundColor: theme.palette.grey[700],
    '& .MuiTableCell-head': {
      color: theme.palette.common.white,
      fontWeight: 600,
    },
  },
  uploadButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 4),
    textTransform: 'none',
    minWidth: '150px',
  },
  progressCell: {
    minWidth: 200,
  },
  progressBar: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  statusText: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  statusSubtext: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  completionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  processingNote: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'left',
  },
  errorText: {
    fontSize: '0.85rem',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
}));

// Helper to parse DICOM metadata from file
const parseDicomMetadata = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const byteArray = new Uint8Array(arrayBuffer);
        const dataSet = dicomParser.parseDicom(byteArray);
        
        const getString = (tag) => {
          try {
            const element = dataSet.elements[tag];
            return element ? dataSet.string(tag) : '';
          } catch (e) {
            return '';
          }
        };
        
        resolve({
          studyInstanceUID: getString('x0020000d'),
          patientName: getString('x00100010'),
          patientBirthDate: getString('x00100030'),
          studyDate: getString('x00080020'),
          studyDescription: getString('x00081030'),
          modality: getString('x00080060'),
        });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Helper to format DICOM date (YYYYMMDD -> MM-DD-YYYY)
const formatDicomDate = (dateStr) => {
  if (!dateStr || dateStr.length !== 8) return '';
  return `${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}-${dateStr.substring(0, 4)}`;
};

// Helper to format patient name (Last^First -> Last, First)
const formatPatientName = (name) => {
  if (!name) return 'Unknown';
  const parts = name.split('^');
  if (parts.length >= 2) {
    return `${parts[0]}, ${parts[1]}`;
  }
  return name;
};

const Upload = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const fileInputRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const [isScanning, setIsScanning] = useState(false);
  const [studies, setStudies] = useState([]);
  const [selectedStudyIds, setSelectedStudyIds] = useState(new Set());
  
  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { studyUID: { progress, status, backendStudyId, taskId, error } }
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);
  const [processingFullyComplete, setProcessingFullyComplete] = useState(false);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Poll processing status for studies
  useEffect(() => {
    if (!allUploadsComplete) return;

    const PROCESSING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes timeout for all studies
    const processingStartTime = Date.now(); // Single shared start time

    const checkProcessingStatus = async () => {
      // Use functional update to access current state without dependency
      setUploadProgress((currentProgress) => {
        const updatedProgress = { ...currentProgress };

        // Check if we've exceeded the timeout window
        const isTimedOut = Date.now() - processingStartTime > PROCESSING_TIMEOUT_MS;

        for (const [studyUID, progress] of Object.entries(currentProgress)) {
          if (progress.status === 'complete' && progress.backendStudyId) {
            
            // Apply timeout to all remaining studies
            if (isTimedOut) {
              updatedProgress[studyUID] = {
                ...progress,
                status: 'processing_failed',
                error: 'Processing timeout - took longer than 5 minutes',
              };
              continue; // Skip API call for timed-out studies
            }

            // Check processing status asynchronously
            apiClient.get(`/studies/${progress.backendStudyId}`)
              .then((response) => {
                if (response.data && response.data.id) {
                  // Processing complete - update state
                  setUploadProgress((prev) => ({
                    ...prev,
                    [studyUID]: {
                      ...prev[studyUID],
                      status: 'processed',
                    },
                  }));
                }
              })
              .catch((error) => {
                if (error.response?.status === 500) {
                  // Processing failed
                  setUploadProgress((prev) => ({
                    ...prev,
                    [studyUID]: {
                      ...prev[studyUID],
                      status: 'processing_failed',
                      error: 'Backend processing failed',
                    },
                  }));
                }
                // Otherwise still processing (404, etc.)
              });
          }
        }

        // Check if we should stop polling
        const shouldStopPolling = Object.values(updatedProgress).every(
          (p) => p.status === 'processed' || p.status === 'processing_failed' || p.status === 'error' || p.status === 'duplicate'
        );
        
        if (shouldStopPolling && pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          
          // Add grace period to ensure backend has fully committed all data
          setTimeout(() => {
            setProcessingFullyComplete(true);
          }, 3000); // 3 second grace period
        }

        // Return updated state
        return updatedProgress;
      });
    };

    // Start polling every 3 seconds
    pollingIntervalRef.current = setInterval(checkProcessingStatus, 3000);
    
    // Check immediately
    checkProcessingStatus();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [allUploadsComplete]);

  const handleBackToLibrary = () => {
    navigate('/library');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChooseFolder = () => {
    fileInputRef.current?.click();
  };

  const handleFolderSelected = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) {
      return;
    }

    setIsScanning(true);
    setStudies([]);
    setSelectedStudyIds(new Set());
    setUploadProgress({});
    setAllUploadsComplete(false);
    setProcessingFullyComplete(false);

    try {
      // Filter for DICOM files and parse metadata
      const dicomFilesWithMetadata = [];
      
      for (const file of files) {
        const isDicom = await isDicomFile(file);
        if (isDicom) {
          try {
            const metadata = await parseDicomMetadata(file);
            dicomFilesWithMetadata.push({ file, metadata });
          } catch (error) {
            console.error('Error parsing DICOM metadata:', file.name, error);
          }
        }
      }

      // Group files by StudyInstanceUID
      const studyMap = new Map();
      
      for (const { file, metadata } of dicomFilesWithMetadata) {
        const studyUID = metadata.studyInstanceUID;
        
        if (!studyUID) continue;
        
        if (!studyMap.has(studyUID)) {
          studyMap.set(studyUID, {
            studyInstanceUID: studyUID,
            patientName: metadata.patientName,
            patientBirthDate: metadata.patientBirthDate,
            studyDate: metadata.studyDate,
            studyDescription: metadata.studyDescription,
            modalities: new Set(),
            files: [],
          });
        }
        
        const study = studyMap.get(studyUID);
        study.files.push(file);
        
        if (metadata.modality) {
          study.modalities.add(metadata.modality);
        }
      }

      // Convert to array and format modalities
      const studiesArray = Array.from(studyMap.values()).map(study => ({
        ...study,
        modality: Array.from(study.modalities).sort().join(', ') || 'N/A',
      }));
            
      setStudies(studiesArray);
      
      // Default: select all studies
      const allStudyIds = new Set(studiesArray.map(s => s.studyInstanceUID));
      setSelectedStudyIds(allStudyIds);
      
    } catch (error) {
      console.error('Error scanning files:', error);
      alert('Error scanning folder. Please try again.');
    } finally {
      setIsScanning(false);
    }

    // Reset file input
    e.target.value = '';
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allStudyIds = new Set(studies.map(s => s.studyInstanceUID));
      setSelectedStudyIds(allStudyIds);
    } else {
      setSelectedStudyIds(new Set());
    }
  };

  const handleStudyToggle = (studyUID) => {
    const newSelected = new Set(selectedStudyIds);
    if (newSelected.has(studyUID)) {
      newSelected.delete(studyUID);
    } else {
      newSelected.add(studyUID);
    }
    setSelectedStudyIds(newSelected);
  };

  const handleUpload = async () => {
    setIsUploading(true);

    // Fetch existing study UIDs right before uploading
    let existingUIDs = new Set();
    try {
      const response = await apiClient.get('/studies', {
        params: {
          page_size: 100,
          page: 1,
        }
      });
      const existingStudies = response.data.studies || [];
      existingUIDs = new Set(existingStudies.map(s => s.study_instance_uid));
    } catch (error) {
      console.error('Failed to fetch existing studies for duplicate check:', error);
      // Continue with empty set - backend 409 will still catch duplicates as fallback
    }
    
    // Get selected studies
    const selectedStudies = studies.filter(s => selectedStudyIds.has(s.studyInstanceUID));
    
    // Initialize progress for all selected studies
    const initialProgress = {};
    selectedStudies.forEach(study => {
      initialProgress[study.studyInstanceUID] = {
        progress: 0,
        status: 'uploading',
        backendStudyId: null,
        taskId: null,
        error: null,
      };
    });
    setUploadProgress(initialProgress);
    
    // Upload each study sequentially
    for (const study of selectedStudies) {

      // Check for duplicate before uploading
      if (existingUIDs.has(study.studyInstanceUID)) {
        setUploadProgress(prev => ({
          ...prev,
          [study.studyInstanceUID]: {
            progress: 0,
            status: 'duplicate',
            error: 'Study already exists in your library',
          },
        }));
        continue;
      }

      try {
        const files = study.files;
        
        // Track progress for this study
        let totalBytes = 0;
        let uploadedBytes = 0;
        
        files.forEach(f => {
          totalBytes += f.size;
        });
        
        // Upload study with progress tracking
        const result = await uploadService.uploadStudy(files, (currentFile, totalFiles, fileProgress) => {
          // Calculate overall progress weighted by file sizes
          const currentFileIndex = currentFile - 1;
          
          // Bytes uploaded from completed files
          let completedBytes = 0;
          for (let i = 0; i < currentFileIndex; i++) {
            completedBytes += files[i].size;
          }
          
          // Add partial progress from current file
          const currentFileBytes = files[currentFileIndex].size * (fileProgress / 100);
          uploadedBytes = completedBytes + currentFileBytes;
          
          const overallProgress = (uploadedBytes / totalBytes) * 100;
          
          setUploadProgress(prev => ({
            ...prev,
            [study.studyInstanceUID]: {
              ...prev[study.studyInstanceUID],
              progress: overallProgress,
              status: 'uploading',
            },
          }));
        });
        
        // Mark as complete with backend study ID and task ID
        setUploadProgress(prev => ({
          ...prev,
          [study.studyInstanceUID]: {
            progress: 100,
            status: 'complete',
            backendStudyId: result.study_id,
            taskId: result.task_id,
            error: null,
          },
        }));
        
      } catch (error) {
        console.error('Upload failed for study:', study.studyInstanceUID, error);
        
        // Check if this is a duplicate error (detected during complete upload)
        if (error.isDuplicate) {
          // Mark as duplicate (not error) - files were uploaded successfully
          setUploadProgress(prev => ({
            ...prev,
            [study.studyInstanceUID]: {
              progress: 100,
              status: 'duplicate',
              error: 'Study already exists in your library',
            },
          }));
          continue; // Skip to next study
        }
        
        // Parse error message for better user feedback
        let errorMessage = error.message;
        
        if (error.message.includes('quota')) {
          errorMessage = 'Storage quota exceeded';
        } else if (error.message.includes('permission')) {
          errorMessage = 'Permission denied';
        } else if (error.message.includes('Network error')) {
          errorMessage = 'Network error - check connection';
        }
        
        // Mark as error - upload failed
        setUploadProgress(prev => ({
          ...prev,
          [study.studyInstanceUID]: {
            progress: 0,
            status: 'error',
            error: errorMessage,
          },
        }));
      }
    }
    
    setIsUploading(false);
    setAllUploadsComplete(true);
  };

  const handleUploadAnother = () => {
    // Reset to IDLE state
    setStudies([]);
    setSelectedStudyIds(new Set());
    setUploadProgress({});
    setAllUploadsComplete(false);
    setProcessingFullyComplete(false);
    
    // Clear polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Determine current state
  const isIdle = !isScanning && studies.length === 0 && !isUploading && Object.keys(uploadProgress).length === 0;
  const hasStudies = !isScanning && studies.length > 0 && !isUploading && Object.keys(uploadProgress).length === 0;
  const showUploadingTable = isUploading || Object.keys(uploadProgress).length > 0;
  
  // Back button visible only in idle, scanning, and hasStudies states
  const showBackButton = isIdle || isScanning || hasStudies;
  
  const allSelected = studies.length > 0 && selectedStudyIds.size === studies.length;
  const someSelected = selectedStudyIds.size > 0 && selectedStudyIds.size < studies.length;

  return (
    <Box className={classes.root}>
      {/* Hidden file input for folder selection */}
      <input
        ref={fileInputRef}
        type="file"
        webkitdirectory="true"
        directory="true"
        multiple
        style={{ display: 'none' }}
        onChange={handleFolderSelected}
      />

      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {showBackButton && (
            <Tooltip title="Back to Library">
              <IconButton onClick={handleBackToLibrary}>
                <Icon path={mdiArrowLeft} size={1} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box className={classes.headerRight}>
          <Tooltip title="Log Out">
            <IconButton 
              onClick={handleLogout}
              style={{visibility: 'hidden' }}
            >
              <Icon path={mdiLogout} size={1} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Content */}
      <Box className={classes.content}>
        {isIdle && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={classes.chooseFolderButton}
            onClick={handleChooseFolder}
          >
            <Icon path={mdiFolderOpen} size={1} className={classes.buttonIcon} />
            Choose Folder
          </Button>
        )}

        {isScanning && (
          <Box className={classes.statusBox}>
            <CircularProgress size={60} />
            <Typography variant="h6">
              Scanning folder for DICOM files...
            </Typography>
          </Box>
        )}

        {hasStudies && (
          <Box className={classes.tableWrapper}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.chooseAnotherButton}
              onClick={handleChooseFolder}
            >
              Choose Another Folder
            </Button>

            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow className={classes.tableHeaderRow}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Study Date</TableCell>
                    <TableCell>Study Description</TableCell>
                    <TableCell>Modality</TableCell>
                    <TableCell align="right"># DICOM Files</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studies.map((study) => (
                    <TableRow
                      key={study.studyInstanceUID}
                      hover
                      onClick={() => handleStudyToggle(study.studyInstanceUID)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={selectedStudyIds.has(study.studyInstanceUID)}
                          onChange={() => handleStudyToggle(study.studyInstanceUID)}
                        />
                      </TableCell>
                      <TableCell>{formatPatientName(study.patientName)}</TableCell>
                      <TableCell>{formatDicomDate(study.patientBirthDate)}</TableCell>
                      <TableCell>{formatDicomDate(study.studyDate)}</TableCell>
                      <TableCell>{study.studyDescription || 'N/A'}</TableCell>
                      <TableCell>{study.modality}</TableCell>
                      <TableCell align="right">{study.files.length}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="contained"
              color="primary"
              className={classes.uploadButton}
              onClick={handleUpload}
              disabled={selectedStudyIds.size === 0}
            >
              Upload {selectedStudyIds.size > 0 ? `(${selectedStudyIds.size})` : ''}
            </Button>
          </Box>
        )}

        {showUploadingTable && (
          <Box className={classes.tableWrapper}>
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow className={classes.tableHeaderRow}>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Study Date</TableCell>
                    <TableCell>Study Description</TableCell>
                    <TableCell>Modality</TableCell>
                    <TableCell align="right"># DICOM Files</TableCell>
                    <TableCell className={classes.progressCell}>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studies
                    .filter(s => selectedStudyIds.has(s.studyInstanceUID))
                    .map((study) => {
                      const progress = uploadProgress[study.studyInstanceUID] || { progress: 0, status: 'pending' };
                      
                      return (
                        <TableRow key={study.studyInstanceUID}>
                          <TableCell>{formatPatientName(study.patientName)}</TableCell>
                          <TableCell>{formatDicomDate(study.patientBirthDate)}</TableCell>
                          <TableCell>{formatDicomDate(study.studyDate)}</TableCell>
                          <TableCell>{study.studyDescription || 'N/A'}</TableCell>
                          <TableCell>{study.modality}</TableCell>
                          <TableCell align="right">{study.files.length}</TableCell>
                          <TableCell className={classes.progressCell}>
                            {progress.status === 'uploading' && (
                              <Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={progress.progress}
                                  className={classes.progressBar}
                                />
                                <Typography variant="body2" color="textSecondary">
                                  {progress.progress.toFixed(0)}%
                                </Typography>
                              </Box>
                            )}
                            {progress.status === 'complete' && (
                              <Box>
                                <Box display="flex" alignItems="center" mb={0.5}>
                                  <Icon path={mdiCheckCircleOutline } size={0.8} color="green" />
                                  <Typography className={classes.statusText} style={{ color: 'green', marginLeft: '5px' }}>
                                    Upload Complete
                                  </Typography>
                                </Box>
                                <Typography className={classes.statusSubtext}>
                                  Processing...
                                </Typography>
                              </Box>
                            )}
                            {progress.status === 'processed' && (
                              <Box>
                                <Box display="flex" alignItems="center" mb={0.5}>
                                  <Icon path={mdiCheckCircleOutline } size={0.8} color="green" />
                                  <Typography className={classes.statusText} style={{ color: 'green', marginLeft: '5px' }}>
                                    Upload Complete
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                  <Icon path={mdiCheckCircleOutline } size={0.8} color="green" />
                                  <Typography className={classes.statusText} style={{ color: 'green', marginLeft: '5px' }}>
                                    Processing Complete
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            {progress.status === 'processing_failed' && (
                              <Box>
                                <Box display="flex" alignItems="center" mb={0.5}>
                                  <Icon path={mdiCheckCircleOutline } size={0.8} color="green" />
                                  <Typography className={classes.statusText} style={{ color: 'green', marginLeft: '5px' }}>
                                    Upload Complete
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                  <Icon path={mdiAlertCircleOutline} size={0.8} color="red" />
                                  <Typography className={classes.statusText} style={{ color: 'red', marginLeft: '5px' }}>
                                    Processing Failed
                                  </Typography>
                                </Box>
                                <Typography className={classes.errorText}>
                                  {progress.error || 'Unknown error'}
                                </Typography>
                              </Box>
                            )}
                            {progress.status === 'duplicate' && (
                              <Box>
                                <Box display="flex" alignItems="center" mb={0.5}>
                                  <Icon path={mdiAlertCircleOutline} size={0.8} color="orange" />
                                  <Typography className={classes.statusText} style={{ color: 'orange', marginLeft: '5px' }}>
                                    Duplicate Detected
                                  </Typography>
                                </Box>
                                <Typography className={classes.statusSubtext}>
                                  {progress.error || 'Study already exists in your library'}
                                </Typography>
                              </Box>
                            )}                            
                            {progress.status === 'error' && (
                              <Box>
                                <Box display="flex" alignItems="center" mb={0.5}>
                                  <Icon path={mdiAlertCircleOutline} size={0.8} color="red" />
                                  <Typography className={classes.statusText} style={{ color: 'red', marginLeft: '5px' }}>
                                    Upload Failed
                                  </Typography>
                                </Box>
                                <Typography className={classes.errorText}>
                                  {progress.error || 'Unknown error'}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Buttons and note below table */}
            {allUploadsComplete && (
              <>
                {(() => {
                  const hasProcessingStudies = Object.values(uploadProgress).some(
                    p => p.status === 'complete' || p.status === 'processed' || p.status === 'processing_failed'
                  );

                  return (
                    <>
                      <Box className={classes.completionButtons}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleUploadAnother}
                          disabled={hasProcessingStudies && !processingFullyComplete}
                        >
                          Upload Another
                        </Button>
                        
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleBackToLibrary}
                          disabled={hasProcessingStudies && !processingFullyComplete}
                        >
                          Back to Library
                        </Button>
                      </Box>

                      {hasProcessingStudies && (
                        <Box className={classes.processingNote}>
                          <Typography variant="body2">
                            {processingFullyComplete ? (
                              <>
                                <strong>Processing Complete!</strong> All studies have been successfully processed 
                                and are ready to view in your Library.
                              </>
                            ) : (
                              <>
                                <strong>Note:</strong> Studies are finishing processing... 
                                Please wait a moment while we ensure everything is ready in your Library.
                              </>
                            )}
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Upload;